'use client'

import { InstructionSize } from '@/app/[locale]/product/[slug]/_components/product-info-sizes/instructions-size'
import { ProductInfoSuggest } from '@/app/[locale]/product/[slug]/_components/product-info-suggest/module'
import { ImageType } from '@/app/[locale]/product/[slug]/_components/product-info-suggest/product-info-suggest-gallery'
import { BasicProductInfo } from '@/app/[locale]/product/[slug]/_components/product-info-suggest/product-info-suggest-modal'
import { ProductInfo } from '@/app/[locale]/product/[slug]/_components/product-info/module'
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { Skeleton } from '@/components/ui/skeleton'
import { useProductListByIds } from '@/hooks/react-query/products/use-product'
import { PRODUCT_SIZE } from '@/lib/constants/size'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { useIsBuy1Get1InGiftShopCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { ProductFormatted, VariantMapped } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { Gift } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface ColorOption {
  title: string
  slug: string
  label: string
  image: Array<{ id: string; src: string }>
}

interface GiftSelectionModalProps {
  product: ProductFormatted
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (variantId: string) => void
  isSelecting: boolean
  mainProductPrice?: number // Giá của sản phẩm chính để so sánh
}

const GiftSelectionModalSkeleton = () => {
  return (
    <div className='items-stretch gap-11 lg:flex'>
      {/* Gallery skeleton */}
      <div className='h-[200px] basis-1/2 max-lg:hidden md:flex md:gap-4 lg:h-full'>
        <Skeleton className='h-full w-full' />
      </div>
      {/* Content skeleton */}
      <div className='flex basis-1/2 flex-col gap-2 bg-light lg:p-0'>
        <div>
          <div className='flex items-start gap-4'>
            {/* Mobile thumbnail skeleton */}
            <Skeleton className='h-[68px] w-[68px] rounded lg:hidden' />
            <div className='flex-1'>
              {/* Title skeleton */}
              <Skeleton className='mb-1 h-7 w-3/4 lg:h-9 lg:w-full' />
              {/* Mobile price skeleton */}
              <div className='mt-2 md:hidden'>
                <Skeleton className='h-5 w-24' />
              </div>
            </div>
          </div>
          <Divider className='my-5 bg-neutral-200' />
          {/* Desktop price skeleton */}
          <div className='max-md:hidden'>
            <Skeleton className='h-6 w-32' />
          </div>
        </div>

        <div className='space-y-4 lg:mt-10'>
          {/* Color variants skeleton */}
          <div className='flex gap-2'>
            {Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton key={idx} className='h-10 w-10 rounded-full' />
            ))}
          </div>
          {/* Size buttons skeleton */}
          <div className='flex flex-wrap gap-2'>
            {Array.from({ length: 6 }).map((_, idx) => (
              <Skeleton key={idx} className='h-10 w-12' />
            ))}
          </div>
          {/* Size guide skeleton */}
          <Skeleton className='h-32 w-full' />
          {/* Button skeleton */}
          <div className='relative mt-4 h-13 lg:!mt-10'>
            <Skeleton className='h-full w-full' />
          </div>
        </div>
      </div>
    </div>
  )
}

export const GiftSelectionModal = ({
  product,
  open,
  onOpenChange,
  onConfirm,
  isSelecting,
  mainProductPrice,
}: GiftSelectionModalProps) => {
  const t = useTranslations()
  const { data: productsData, isLoading } = useProductListByIds(
    [product.id],
    undefined,
    open
  )
  const productDetail = productsData?.[0]
  const isBuy1Get1InGiftShopActive = useIsBuy1Get1InGiftShopCampaignActive()

  // Close modal if campaign is not active
  useEffect(() => {
    if (open && !isBuy1Get1InGiftShopActive) {
      onOpenChange(false)
    }
  }, [open, isBuy1Get1InGiftShopActive, onOpenChange])

  // Map product detail to BasicProductInfo format
  const basicProductInfo: BasicProductInfo | null = useMemo(() => {
    if (!productDetail) return null

    return {
      id: productDetail.id,
      display_name:
        productDetail.display_name || productDetail.title || product.title,
      seo_alias:
        productDetail.seo_alias || product.href.replace('/product/', ''),
      regular_price: productDetail.regular_price || product.regular_price,
      compare_price: productDetail.compare_price || product.compare_price,
      percent:
        productDetail.percent ||
        productDetail.sale_percent ||
        product.sale_percent,
      default_thumbnail:
        productDetail.default_thumbnail || product.default_thumbnail,
      mapped_variants: productDetail.mapped_variants || product.mapped_variants,
      images: productDetail.images,
      options_value: productDetail.options_value,
      sizechart: productDetail.sizechart,
      gender_type: productDetail.gender_type,
      size_guide: productDetail.size_guide,
    }
  }, [productDetail, product])

  // State cho màu và size được chọn
  const [selectedColorId, setSelectedColorId] = useState<string>('')
  const [selectedSizeId, setSelectedSizeId] = useState<string>('')

  // Xử lý ảnh gallery dựa trên màu được chọn
  const galleryImages: ImageType[] = useMemo(() => {
    if (
      !basicProductInfo?.options_value?.[0] ||
      !selectedColorId ||
      !basicProductInfo.options_value[0].options
    )
      return []

    const colorOption = basicProductInfo.options_value[0]?.options?.find(
      (opt: ColorOption) => opt.title === selectedColorId
    )

    const images = colorOption?.image || basicProductInfo?.images || []
    const galleryImages = images.slice(0, -1)

    return galleryImages.map((img: { src: string; id?: string }) => ({
      url: img.src,
      alt: basicProductInfo.display_name || 'Product image',
    }))
  }, [basicProductInfo, selectedColorId])

  // Lấy mảng màu sắc từ mapped_variants
  const colorVariants = useMemo(() => {
    if (!basicProductInfo?.mapped_variants) return []

    const colors = Object.keys(basicProductInfo.mapped_variants || {})

    return colors.map((color) => {
      const firstVariant = basicProductInfo.mapped_variants?.[color]?.[0]
      return {
        imageUrl: firstVariant?.colorImage?.toString() || '',
        title: color,
        id: color,
      }
    })
  }, [basicProductInfo?.mapped_variants])

  // Update selectedColorId when variants change
  useEffect(() => {
    if (colorVariants.length > 0 && !selectedColorId) {
      setSelectedColorId(colorVariants[0].id)
    }
  }, [colorVariants, selectedColorId])

  // Lấy các size phù hợp với màu đã chọn và thông tin isDisable
  const availableSizes = useMemo(() => {
    if (!basicProductInfo?.mapped_variants || !selectedColorId) return []

    const variantsForSelectedColor =
      basicProductInfo.mapped_variants[selectedColorId] || []

    const sizeMap = new Map<
      string,
      { isDisabled: boolean; variant: VariantMapped }
    >()

    variantsForSelectedColor.forEach((variant) => {
      if (!variant.size) return

      const sizeKey = variant.size.toString()

      // Check if variant is disabled due to stock or price
      const variantPrice =
        variant.regular_price || basicProductInfo.regular_price || 0
      const isPriceTooHigh =
        mainProductPrice !== undefined && variantPrice > mainProductPrice
      const isVariantDisabled = variant.isDisable || isPriceTooHigh

      if (
        !sizeMap.has(sizeKey) ||
        (!isVariantDisabled && sizeMap.get(sizeKey)?.isDisabled)
      ) {
        sizeMap.set(sizeKey, {
          isDisabled: isVariantDisabled,
          variant,
        })
      }
    })

    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']
    const sizeOrderMap = new Map(sizeOrder.map((size, index) => [size, index]))

    return Array.from(sizeMap.entries())
      .map(([size, data]) => ({
        title: size,
        id: size.toLowerCase(),
        isDisabled: data.isDisabled,
      }))
      .sort((a, b) => {
        const indexA = sizeOrderMap.get(a.title) ?? Infinity
        const indexB = sizeOrderMap.get(b.title) ?? Infinity
        return indexA - indexB
      })
  }, [
    basicProductInfo?.mapped_variants,
    basicProductInfo?.regular_price,
    selectedColorId,
    mainProductPrice,
  ])

  // Cập nhật selectedSizeId khi availableSizes thay đổi
  useEffect(() => {
    // Nếu sản phẩm không có size, reset selectedSizeId về empty string
    if (availableSizes.length === 0) {
      setSelectedSizeId('')
      return
    }

    const firstAvailableSize = availableSizes.find((size) => !size.isDisabled)

    if (
      availableSizes.length > 0 &&
      (!selectedSizeId ||
        !availableSizes.some((s) => s.id === selectedSizeId && !s.isDisabled))
    ) {
      setSelectedSizeId(
        firstAvailableSize ? firstAvailableSize.id : availableSizes[0].id
      )
    }
  }, [availableSizes, selectedSizeId])

  // Handlers
  const handleColorChange = useCallback((colorId: string) => {
    setSelectedColorId(colorId)
  }, [])

  const handleSizeChange = useCallback((sizeId: string) => {
    setSelectedSizeId(sizeId)
  }, [])

  // Tìm variant được chọn
  const selectedVariant = useMemo(() => {
    if (!basicProductInfo?.mapped_variants || !selectedColorId) return null

    const variantsForSelectedColor =
      basicProductInfo.mapped_variants[selectedColorId] || []

    // Nếu sản phẩm không có size (availableSizes.length === 0)
    // Tìm variant không có size hoặc size là empty
    if (availableSizes.length === 0) {
      return variantsForSelectedColor.find(
        (variant) => !variant.size || variant.size === ''
      )
    }

    // Nếu có size, tìm variant theo size được chọn
    if (!selectedSizeId) return null

    return variantsForSelectedColor.find(
      (variant) => variant.size?.toLowerCase() === selectedSizeId
    )
  }, [
    basicProductInfo?.mapped_variants,
    selectedColorId,
    selectedSizeId,
    availableSizes.length,
  ])

  // Loading state cho button khi đang xác nhận
  const [isConfirming, setIsConfirming] = useState(false)

  // Handle confirm
  const handleConfirm = useCallback(async () => {
    if (!selectedVariant || isConfirming) return

    setIsConfirming(true)
    try {
      await onConfirm(selectedVariant.id)

      onOpenChange(false)
    } catch (error) {
      // Nếu có lỗi, không đóng modal để user có thể thử lạn
      console.error('Error confirming gift selection:', error)
    } finally {
      setIsConfirming(false)
    }
  }, [selectedVariant, onConfirm, onOpenChange, isConfirming])

  // Check if button should be disabled
  const variantPrice =
    selectedVariant?.regular_price || basicProductInfo?.regular_price || 0
  const isPriceTooHigh =
    mainProductPrice !== undefined && variantPrice > mainProductPrice
  const isDisabled =
    !selectedVariant ||
    selectedVariant.isDisable ||
    isPriceTooHigh ||
    (selectedVariant.quantity !== undefined && selectedVariant.quantity <= 0)

  // Handler để prevent modal đóng khi PhotoSwipe đang mở
  const handleInteractOutside = useCallback((e: Event) => {
    // Check xem click có phải từ PhotoSwipe container không
    const target = e.target as HTMLElement
    if (target?.closest('.pswp')) {
      e.preventDefault()
    }
  }, [])

  // Handler để prevent modal đóng khi nhấn Escape và PhotoSwipe đang mở
  const handleEscapeKeyDown = useCallback((e: KeyboardEvent) => {
    // Check xem PhotoSwipe có đang mở không
    const pswpElement = document.querySelector('.pswp')
    if (pswpElement && pswpElement.classList.contains('pswp--open')) {
      e.preventDefault()
    }
  }, [])

  let sizeType = ''
  if (basicProductInfo?.gender_type === 'FEMALE') {
    sizeType = PRODUCT_SIZE[basicProductInfo?.sizechart || '']
  } else {
    sizeType = basicProductInfo?.sizechart || ''
  }

  const buttonText = isSelecting
    ? t('gift_shop.select_gift')
    : t('gift_shop.exchange_gift')
  // Don't render modal if campaign is not active
  if (!isBuy1Get1InGiftShopActive) {
    return null
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent
        className='z-[102] gap-0 border-none px-7 py-10 lg:w-[1064px] lg:max-w-full lg:rounded-md'
        aria-describedby={'modal-gift-selection'}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <ResponsiveModalTitle className='mb-4.5 font-criteria text-2xl font-semibold leading-7 text-primary lg:text-[28px] lg:leading-9.5'>
          {buttonText}
        </ResponsiveModalTitle>
        <ResponsiveModalDescription />
        {isLoading || !basicProductInfo ? (
          <GiftSelectionModalSkeleton />
        ) : (
          <div className='items-stretch gap-11 lg:flex'>
            <ProductInfoSuggest.Gallery
              images={galleryImages}
              className='h-[200px] basis-1/2 max-lg:hidden md:flex md:gap-4 lg:h-full [&_.pswp-gallery]:h-full'
            />
            <div className={'flex basis-1/2 flex-col gap-2 bg-light lg:p-0'}>
              <div>
                <div className='flex items-start gap-4'>
                  <Image
                    src={ImageHelper.getImageUrl(
                      basicProductInfo?.default_thumbnail || '',
                      '',
                      136
                    )}
                    alt={basicProductInfo?.display_name || 'Product'}
                    width={68}
                    height={68}
                    className='rounded lg:hidden'
                  />
                  <div>
                    <h1 className='line-clamp-2 font-sans text-lg font-medium leading-6 text-neutral-900 lg:mb-1 lg:text-[28px] lg:leading-9'>
                      {basicProductInfo?.display_name}
                    </h1>
                    <div className='md:hidden'>
                      <ProductInfo.Price
                        product={basicProductInfo as ProductDetailFormatted}
                        comparePrice={basicProductInfo?.compare_price || 0}
                        regularPrice={basicProductInfo?.regular_price || 0}
                        salePercent={basicProductInfo?.percent || 0}
                      />
                    </div>
                  </div>
                </div>

                <Divider className='my-5 bg-neutral-200' />
                <div className='max-md:hidden'>
                  <ProductInfo.Price
                    product={basicProductInfo as ProductDetailFormatted}
                    comparePrice={basicProductInfo?.compare_price || 0}
                    regularPrice={basicProductInfo?.regular_price || 0}
                    salePercent={basicProductInfo?.percent || 0}
                  />
                </div>
              </div>

              <div className='space-y-4 lg:mt-10'>
                <ProductInfoSuggest.Variants
                  variants={colorVariants}
                  selectedVariantId={selectedColorId}
                  onVariantChange={handleColorChange}
                />
                <ProductInfoSuggest.Sizes
                  sizes={availableSizes}
                  selectedSizeId={selectedSizeId}
                  onSizeChange={handleSizeChange}
                />
                <InstructionSize
                  sizeChart={sizeType}
                  gender_type={basicProductInfo.gender_type || ''}
                  size_guide={
                    basicProductInfo.size_guide || {
                      rows: [],
                      columns: [],
                      headerTitle: '',
                      leftTitle: '',
                      matrix: [],
                    }
                  }
                  productId={basicProductInfo.id}
                  // NOTE: chưa chắc đúng data
                  product={basicProductInfo as ProductDetailFormatted}
                />
                <div className='relative mt-4 h-13 lg:!mt-10'>
                  <Button
                    className='h-full w-full gap-2'
                    variant={'black'}
                    onClick={handleConfirm}
                    disabled={isDisabled || isConfirming}
                  >
                    {isConfirming ? (
                      <>
                        <div className='h-4 w-4 animate-spin rounded-full border-2 border-light border-t-transparent max-lg:hidden' />
                        <p className='font-criteria text-base font-medium normal-case leading-6 max-lg:pl-10'>
                          {t('gift_shop.selecting') || 'Đang chọn quà...'}
                        </p>
                      </>
                    ) : (
                      <>
                        <Gift size={16} className='h-4 w-4 max-lg:hidden' />
                        <p className='font-criteria text-base font-medium normal-case leading-6 max-lg:pl-10'>
                          {isDisabled
                            ? t('product_detail.title.out_of_stock') ||
                              'Hết hàng'
                            : buttonText}
                        </p>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
